import { create } from 'zustand';
import { CategoriaCardapio, ItemCarrinho, UnidadeDeliveryLista, UnidadeDeliveryPublico } from '../delivery.types';
import { carregarCache, limparCache, salvarCache } from './pedidoCache';

interface DeliveryState {
  step: number;
  setStep: (step: number) => void;

  tenant: string;
  setTenant: (tenant: string) => void;

  unidades: UnidadeDeliveryLista[];
  setUnidades: (u: UnidadeDeliveryLista[]) => void;

  unidade: UnidadeDeliveryPublico | null;
  setUnidade: (u: UnidadeDeliveryPublico) => void;

  catalogo: CategoriaCardapio[];
  setCatalogo: (c: CategoriaCardapio[]) => void;

  carrinho: ItemCarrinho[];
  adicionarItem: (item: ItemCarrinho) => void;
  removerItem: (id: string) => void;
  atualizarQuantidade: (id: string, quantidade: number) => void;
  atualizarItem: (id: string, item: ItemCarrinho) => void;
  limparCarrinho: () => void;

  nomeContato: string;
  setNomeContato: (v: string) => void;
  whatsappContato: string;
  setWhatsappContato: (v: string) => void;
  tipoRetirada: 'ENTREGA' | 'RETIRADA';
  setTipoRetirada: (v: 'ENTREGA' | 'RETIRADA') => void;
  enderecoEntrega: string;
  setEnderecoEntrega: (v: string) => void;
  codigoTipoTitulo: number | null;
  setCodigoTipoTitulo: (v: number | null) => void;
  observacaoGeral: string;
  setObservacaoGeral: (v: string) => void;

  codigoSolicitacao: number | null;
  setCodigoSolicitacao: (v: number | null) => void;

  // cache
  restaurarDoCache: () => boolean;
  limparCacheAtual: () => void;

  totalCarrinho: () => number;
  totalComTaxa: () => number;
}

export const useDeliveryStore = create<DeliveryState>((set, get) => {

  function persistir(patch: Partial<DeliveryState>) {
    // executa após o set — usa o estado já atualizado
    setTimeout(() => {
      const s = get();
      if (!s.tenant || !s.unidade) return;
      salvarCache(s.tenant, s.unidade.codigo, {
        carrinho: s.carrinho,
        step: s.step,
        nomeContato: s.nomeContato,
        whatsappContato: s.whatsappContato,
        tipoRetirada: s.tipoRetirada,
        codigoTipoTitulo: s.codigoTipoTitulo,
        observacaoGeral: s.observacaoGeral,
      });
    }, 0);
    return patch;
  }

  return {
    step: 0,
    setStep: (step) => { set(persistir({ step })); },

    tenant: '',
    setTenant: (tenant) => set({ tenant }),

    unidades: [],
    setUnidades: (unidades) => set({ unidades }),

    unidade: null,
    setUnidade: (unidade) => set({ unidade }),

    catalogo: [],
    setCatalogo: (catalogo) => set({ catalogo }),

    carrinho: [],
    adicionarItem: (item) => set((s) => persistir({ carrinho: [...s.carrinho, item] })),
    removerItem: (id) => set((s) => persistir({ carrinho: s.carrinho.filter((i) => i.id !== id) })),
    atualizarQuantidade: (id, quantidade) =>
      set((s) => persistir({ carrinho: s.carrinho.map((i) => (i.id === id ? { ...i, quantidade } : i)) })),
    atualizarItem: (id, item) =>
      set((s) => persistir({ carrinho: s.carrinho.map((i) => (i.id === id ? { ...item, id } : i)) })),
    limparCarrinho: () => set(persistir({ carrinho: [] })),

    nomeContato: '',
    setNomeContato: (nomeContato) => set(persistir({ nomeContato })),
    whatsappContato: '',
    setWhatsappContato: (whatsappContato) => set(persistir({ whatsappContato })),
    tipoRetirada: 'ENTREGA',
    setTipoRetirada: (tipoRetirada) => set(persistir({ tipoRetirada })),
    enderecoEntrega: '',
    setEnderecoEntrega: (enderecoEntrega) => set({ enderecoEntrega }),
    codigoTipoTitulo: null,
    setCodigoTipoTitulo: (codigoTipoTitulo) => set(persistir({ codigoTipoTitulo })),
    observacaoGeral: '',
    setObservacaoGeral: (observacaoGeral) => set(persistir({ observacaoGeral })),

    codigoSolicitacao: null,
    setCodigoSolicitacao: (codigoSolicitacao) => {
      set({ codigoSolicitacao });
      // pedido finalizado — limpa o cache
      if (codigoSolicitacao != null) {
        const s = get();
        if (s.tenant && s.unidade) limparCache(s.tenant, s.unidade.codigo);
      }
    },

    restaurarDoCache: () => {
      const s = get();
      if (!s.tenant || !s.unidade) return false;
      const cache = carregarCache(s.tenant, s.unidade.codigo);
      if (!cache || cache.carrinho.length === 0) return false;
      set({
        carrinho: cache.carrinho,
        step: cache.step > 0 ? cache.step : 1,
        nomeContato: cache.nomeContato,
        whatsappContato: cache.whatsappContato,
        tipoRetirada: cache.tipoRetirada,
        codigoTipoTitulo: cache.codigoTipoTitulo,
        observacaoGeral: cache.observacaoGeral,
      });
      return true;
    },

    limparCacheAtual: () => {
      const s = get();
      if (s.tenant && s.unidade) limparCache(s.tenant, s.unidade.codigo);
    },

    totalCarrinho: () =>
      get().carrinho.reduce((acc, item) => {
        const extrasCompl = item.complementos
          .filter((c) => c.tipo === 'ADICIONAR')
          .reduce((s, c) => s + c.valorUnitario * c.quantidade, 0);
        const extrasGrupos = item.grupoSelecoes.reduce((s, g) => {
          if (!g.itens.length) return s;
          if (g.estrategiaPreco === 'MAIOR_VALOR') {
            return s + Math.max(...g.itens.map((i) => i.valorAdicional));
          }
          return s + g.itens.reduce((gs, i) => gs + i.valorAdicional, 0);
        }, 0);
        return acc + (item.valorUnitario + extrasCompl + extrasGrupos) * item.quantidade;
      }, 0),

    totalComTaxa: () => {
      const { unidade, tipoRetirada } = get();
      const taxa = tipoRetirada === 'ENTREGA' ? (unidade?.taxaEntrega ?? 0) : 0;
      return get().totalCarrinho() + taxa;
    },
  };
});
